import { useQuery } from "@tanstack/react-query";
import { format, subDays } from "date-fns";
import { useMemo } from "react";
import { DashboardHero } from "@/components/dashboard-hero";
import { ManagerLaunchChecklist } from "@/components/manager-launch-checklist";
import { ManagerQuickActions } from "@/components/manager-quick-actions";
import { PageContainer } from "@/components/page-container";
import { PanelHeader } from "@/components/panel-header";
import { ManagerDashboardCharts } from "@/components/report/manager-dashboard-charts";
import { RecentSignInsTable } from "@/components/report/recent-signins-table";
import { SiteBreakdownTable } from "@/components/report/site-breakdown-table";

import { SummaryCards } from "@/components/summary-cards";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import type { SummaryMetric } from "@/lib/report-metrics";
import { createSummaryMetrics } from "@/lib/report-metrics";
import { trpc } from "@/utils/trpc";

export default function ManagerDashboard() {
  const dateRange = useMemo(() => {
    const end = new Date();
    const start = subDays(end, 6);
    return { start, end };
  }, []);

  const { start, end } = dateRange;

  const reportQuery = useQuery({
    ...trpc.getManagerReport.queryOptions({
      startDate: format(start, "yyyy-MM-dd"),
      endDate: format(end, "yyyy-MM-dd"),
      reportType: "attendance",
    }),
    placeholderData: (previousData) => previousData,
  });
  const managedSitesQuery = useQuery({
    ...trpc.getManagedSites.queryOptions(),
    placeholderData: (previousData) => previousData,
  });

  const summaryCards = useMemo<SummaryMetric[]>(
    () =>
      createSummaryMetrics({
        report: reportQuery.data,
      }),
    [reportQuery.data]
  );

  const siteBreakdown = reportQuery.data?.siteBreakdown ?? [];
  const recentSignIns = reportQuery.data?.recentSignIns ?? [];

  return (
    <>
      <div className="relative z-20 lg:hidden">
        <ManagerQuickActions />
      </div>

      <div className="relative isolate min-h-full overflow-x-hidden">
        <div className="fixed inset-0 bg-[#f7f7f7] bg-[image:var(--dashboard-hero-image)] grayscale bg-cover bg-center bg-no-repeat" />
        <PageContainer
          variant="dashboard"
          className="relative z-10 flex min-h-full flex-col gap-6 lg:gap-8"
        >
          <DashboardHero />

          <ManagerLaunchChecklist
            sites={managedSitesQuery.data}
            report={reportQuery.data}
            isLoading={managedSitesQuery.isLoading || reportQuery.isLoading}
          />

          <SummaryCards metrics={summaryCards} />

          {reportQuery.isError ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
              Unable to load dashboard activity right now. Refresh the page to
              try again.
            </div>
          ) : (
            <>
              <ManagerDashboardCharts
                report={reportQuery.data}
                isLoading={reportQuery.isLoading}
                startDate={start}
                endDate={end}
                firstSiteId={managedSitesQuery.data?.[0]?.id}
              />

              <div className="grid gap-6 xl:grid-cols-2">
                <Card className="h-full">
                  <CardHeader className="pb-2">
                    <PanelHeader
                      title="Recent sign-ins"
                      description="Latest activity from your workforce in the last week."
                    />
                  </CardHeader>
                  <CardContent>
                    <RecentSignInsTable
                      rows={recentSignIns}
                      isLoading={reportQuery.isLoading}
                      limit={5}
                      emptyLabel="Open the site QR code and run the first check-in to start filling this table."
                    />
                  </CardContent>
                </Card>

                <Card className="h-full">
                  <CardHeader className="pb-2">
                    <PanelHeader
                      title="Site coverage"
                      description="Latest sign-in coverage across your managed sites."
                    />
                  </CardHeader>
                  <CardContent>
                    <SiteBreakdownTable
                      rows={siteBreakdown}
                      isLoading={reportQuery.isLoading}
                      limit={5}
                      emptyLabel="Create and activate a site, then ask workers to check in from the QR code."
                    />
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </PageContainer>
      </div>
    </>
  );
}
